import isElectron from 'is-electron';
import { isAuthServiceConfigured } from 'flow/authservice/authservice.config';
import { fromRuntime } from 'runtime-config';

const production = process.env.NODE_ENV === 'production';
const test = process.env.NODE_ENV === 'test'; // Jest sets the NODE_ENV to test
const version = `${process.env.VERSION}${
    process.env.NO_UPDATE === 'true' ? '-NU' : ''
}`;
const beta = version.indexOf('beta') !== -1;

export const dev = !production && !beta;

// Electron: populate window.__RUNTIME_CONFIG__ from the .env file read by the main process.
// Must run before anything below reads from runtime config (e.g. isAuthServiceConfigured).
if (isElectron() && !test) {
    const ipcRenderer = require('electron')?.ipcRenderer;
    const envObject = ipcRenderer?.sendSync('get-env');
    // The renderer's real environment wins over the packaged .env file,
    // matching dotenv's no-override semantics in the main process. In dev
    // (start-hot) there is no .env file and APP_FLOW_URL comes from the npm
    // script's environment instead.
    const env = { ...(envObject?.parsed || {}), ...process.env };
    window.__RUNTIME_CONFIG__ = {
        apiUrl: env.APP_FLOW_URL || 'https://flow.telavox.com',
        authServiceUrl: env.APP_AUTHSERVICE_URL || '',
        authServiceRealm: env.APP_AUTHSERVICE_REALM || 'telavox',
        authServiceClientId: env.APP_AUTHSERVICE_CLIENTID || 'web-client',
        regionGeonexService: env.REGION_GEONEX_SERVICE || '',
        regionLookupEnabled: env.REGION_LOOKUP_ENABLED === 'true',
        uaePassEnabled: env.UAE_PASS_ENABLED === 'true',
        faroCollectorUrl:
            env.FARO_COLLECTOR_URL || 'https://telemetry.telavox.com/collect',
        proxyUrl: dev ? env.PROXY_HOST || null : null,
    };
}

const authServiceEnabled = isAuthServiceConfigured();

const environment = ((p, b) => {
    if (p) {
        return b ? 'beta' : 'production';
    } else {
        return 'dev';
    }
})(production, beta);

export const apiUrl = fromRuntime('apiUrl');

export const proxyUrl = dev ? fromRuntime('proxyUrl') : null;

export const isGeonexConfigured = !!fromRuntime('regionGeonexService');
/**
 * AC-893 : Done this for the initial implementation since this enablement will be
 * only within telavox domain
 */
const aiStudioBaseUrl = `https://ai${production ? '' : '-dev'}.telavox.com`;

const topEnabled = ((p) => {
    return p ? true : process.env.TOP_ENABLED === 'false' ? false : true;
})(production);

const localVideoSignaling = process.env.LOCAL_VIDEO_SIGNALING === 'true';
const localVideoChat = process.env.LOCAL_VIDEO_CHAT === 'true';

export const isUaePassEnabled = !!fromRuntime('uaePassEnabled');
export const faroCollectorUrl = fromRuntime('faroCollectorUrl');
// window.__TELEMETRY_ENV__ is expected to be absent in dev/Electron/pre-Slice-1
// images, so this must degrade to '' rather than throw like fromRuntime() does.
export const telemetryApiKey = window.__TELEMETRY_ENV__?.apiKey || '';

const appConfig = Object.freeze({
    production,
    version,
    beta,
    dev,
    environment,
    apiUrl,
    topEnabled,
    localVideoSignaling,
    localVideoChat,
    authServiceEnabled,
    isGeonexConfigured,
    isUaePassEnabled,
    faroCollectorUrl,
    telemetryApiKey,
    aiStudioBaseUrl,
    proxyUrl,
});

if (!appConfig.production && !test) {
    console.group('Application config'); // eslint-disable-line no-console
    for (const o in appConfig) {
        console.log(`${o} => ${appConfig[o]}`); // eslint-disable-line no-console
    }
    console.groupEnd(); // eslint-disable-line no-console
}

/**
 * The application configuration - relies on window.__RUNTIME_CONFIG__ set by the
 * container entrypoint (web) or the Electron IPC handler (desktop).
 */
export default appConfig;
